import * as clack from '@clack/prompts'
import path from 'node:path'
import type { RemoveSchemaCommandOptions } from '@cli-types/core-engine/commands.js'
import { BARREL_SCOPE, UNSUPPORTED_PROJECT_STATE_MESSAGE } from '@cli-constants/snapshots.js'
import { removePath } from '@cli-utils/paths.js'
import { isInteractiveSession } from '@cli-utils/terminal.js'
import { toKebabCase } from '@cli-utils/text.js'
import { loadSchema } from '@core-engine/schema/schema-reader/load-schema.js'
import { clearTombstone } from '@core-engine/snapshots/store/clear-tombstone.js'
import { deleteSnapshot } from '@core-engine/snapshots/store/delete-snapshot.js'
import { hasTombstone } from '@core-engine/snapshots/store/has-tombstone.js'
import { loadManifest } from '@core-engine/snapshots/store/load-manifest.js'
import { snapshotRootExists } from '@core-engine/snapshots/store/snapshot-root-exists.js'
import { writeTombstone } from '@core-engine/snapshots/store/write-tombstone.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { resolveConfigOrExit } from '@next-adapter/config/resolve-config-or-exit.js'
import { applyGeneratedFiles } from '@next-adapter/snapshots/apply/apply-generated-files.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels/render-barrel-files.js'
import { cleanupSchemaEmptyDirs } from './cleanup-schema-empty-dirs.js'
import { resolveSchemaOwnerForRemoval } from './resolve-schema-owner-for-removal.js'

export async function runRemoveSchemaCommand(
  schemaName: string,
  options: RemoveSchemaCommandOptions
): Promise<void> {
  const owner = resolveSchemaOwnerForRemoval(
    options.cwd ? path.resolve(options.cwd) : process.cwd(),
    schemaName
  )
  if (owner === 'core') {
    clack.log.error(
      `"${schemaName}" is a core Admin module and cannot be removed with remove-schema.`
    )
    process.exit(1)
  }
  if (owner.startsWith('preset:')) {
    clack.log.error(`"${schemaName}" is owned by ${owner}. Remove the owning preset instead.`)
    process.exit(1)
  }

  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const config = await resolveConfigOrExit(cwd)
  const paths = resolveProjectPaths(config)
  const manifest = loadManifest(cwd, schemaName)

  if (!snapshotRootExists(cwd)) {
    clack.log.error(UNSUPPORTED_PROJECT_STATE_MESSAGE)
    process.exit(1)
  }

  if (!manifest) {
    clack.log.error(UNSUPPORTED_PROJECT_STATE_MESSAGE)
    process.exit(1)
  }

  if (!options.force) {
    if (!isInteractiveSession()) {
      clack.log.error(
        `Removing generated files for ${schemaName} needs confirmation. Re-run with --force.`
      )
      process.exit(1)
    }

    const confirmed = await clack.confirm({
      message: `Remove generated files for ${schemaName}?`,
      initialValue: false
    })
    if (clack.isCancel(confirmed) || !confirmed) {
      clack.cancel('Cancelled.')
      return
    }
  }

  const deletedPaths: string[] = []

  for (const file of [...manifest.files.map((entry) => entry.path), ...manifest.skipped]) {
    if (removePath(cwd, file)) {
      deletedPaths.push(file)
    }
  }

  const loaded = (() => {
    try {
      return loadSchema(path.join(cwd, ...paths.schemasDir.split('/')), schemaName)
    } catch {
      return null
    }
  })()

  const kebabName = toKebabCase(schemaName)
  if (loaded?.type === 'form') {
    if (removePath(cwd, `${paths.adminActionsDir}/${schemaName}`)) {
      deletedPaths.push(`${paths.adminActionsDir}/${schemaName}`)
    }
    if (removePath(cwd, `${paths.pagesDir}/forms/${kebabName}`)) {
      deletedPaths.push(`${paths.pagesDir}/forms/${kebabName}`)
    }
  } else {
    if (removePath(cwd, `${paths.adminActionsDir}/${schemaName}`)) {
      deletedPaths.push(`${paths.adminActionsDir}/${schemaName}`)
    }
    if (removePath(cwd, `${paths.pagesDir}/${schemaName}`)) {
      deletedPaths.push(`${paths.pagesDir}/${schemaName}`)
    }
  }

  cleanupSchemaEmptyDirs(cwd, deletedPaths, paths)
  deleteSnapshot(cwd, schemaName)
  if (hasTombstone(cwd, schemaName)) {
    clearTombstone(cwd, schemaName)
  }
  writeTombstone(cwd, schemaName)

  await applyGeneratedFiles({
    cwd,
    config,
    scope: BARREL_SCOPE,
    schemaJson: { name: BARREL_SCOPE },
    generatedFiles: renderBarrelFiles(cwd, config),
    force: false,
    interactive: false
  })

  clack.log.info(
    `Tombstone written: .betterstart/snapshots/_removed/${schemaName}\nSchema JSON preserved.`
  )
  clack.outro(`Removed generated files for ${schemaName}`)
}
